import React, {useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {getPersistedDisplayUsers, getToken, setPersistedDisplayUsers, setToken} from '../utils/auth';
import {getUser} from '../services/auth-service';
import {useEventsService} from "../services/events-service";
import {AppEvents} from "../utils/constants";

const AuthContext = React.createContext();

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired
};

export function AuthProvider ({ children }) {
  const [initializing, setInitializing] = useState(true);
  const [currentUser, _setCurrentUser] = useState(null);
  const [displayUsers, _setDisplayUsers] = useState(null);
  const authenticated = useMemo(() => !!currentUser, [currentUser]);

  const eventsService = useEventsService();

  const initAuth = () => {
    return getToken()
      ? getUser()
      : Promise.resolve(null);
  };

  //were extending user object with some helpers
  const setCurrentUser = (user = null) => {
    if (user){
      let allRoles = [];
      let allAbilities = [];

      if (user.global_roles && user.global_roles.length>0){
        user.global_roles.map((role)=>{
          allRoles.push(role.name);
          if (role.abilities && role.abilities.length>0){
            role.abilities.map((ability)=>{
              allAbilities.push(ability.name);
            })
          }
        });
      }
      user._all_abilities = allAbilities;
      user._all_roles = allRoles;
    }
    _setCurrentUser(user);
  };

  const setDisplayUsers = (users) => {
    _setDisplayUsers(users);
    setPersistedDisplayUsers(users);
  }
  const doLogout = () => {
    setCurrentUser(null);
    setToken(null);
    setDisplayUsers(null);
  };

  const toggleDisplayUser = (userId) =>{
    let newDisplayUsers = displayUsers ? [...displayUsers] : [];
    if (newDisplayUsers.includes(userId)){
      newDisplayUsers = newDisplayUsers.filter(item=>item!==userId);
    } else {
      newDisplayUsers = [...newDisplayUsers, userId];
    }
    setDisplayUsers(newDisplayUsers && newDisplayUsers.length ? [...newDisplayUsers] : null);
    eventsService.trigger(AppEvents.displayUsersChanged, newDisplayUsers);
  };

  function doAppUserLogin(user){
    //set user with roles, mapping and stuff
    setCurrentUser(Object.assign({},user));
    initDisplayUsers(user.user_groups.length ? user.user_groups[0] : null);
  }

  function initDisplayUsers(userGroup){
    //set displayUsers
    let persistedUsers = getPersistedDisplayUsers();
    if (persistedUsers === null && userGroup?.users?.length){
      //on first init we set all displayusers, otherwise we load selected users user picked manually
      persistedUsers = userGroup.users.map((u)=>{
        return u.id;
      })
    }
    try{
      setDisplayUsers(persistedUsers && persistedUsers.length ? [...persistedUsers] : null);
      eventsService.trigger(AppEvents.displayUsersChanged, [...persistedUsers]);
    } catch (e) {
      setDisplayUsers(null);
      eventsService.trigger(AppEvents.displayUsersChanged, null);
    }
  }

  useEffect(() => {
    initAuth().then((user) => {
      if (!user){
        doLogout();
      } else {
        doAppUserLogin(user);
      }
      setInitializing(false);
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      initializing,
      authenticated,
      currentUser,
      doLogout,
      doAppUserLogin,
      initDisplayUsers,
      setToken,
      setCurrentUser,
      displayUsers,
      setDisplayUsers,
      toggleDisplayUser
    }}> { children }
    </AuthContext.Provider>
  );
}

export function useAuth () {
  const {
    initializing,
    authenticated,
    currentUser,
    doLogout,
    doAppUserLogin,
    initDisplayUsers,
    setToken,
    setCurrentUser,
    displayUsers,
    setDisplayUsers,
    toggleDisplayUser,

    ...context
  } = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return {
    initializing,
    authenticated,
    currentUser,
    doLogout,
    doAppUserLogin,
    initDisplayUsers,
    setToken,
    setCurrentUser,
    displayUsers,
    setDisplayUsers,
    toggleDisplayUser
  };
}
